import { useRef, useMemo, useState, useEffect, useCallback, createContext } from 'react';

import { useLocalStorage } from 'src/hooks/use-local-storage';

import axios, { endpoints } from 'src/utils/axios';

import { useAuthContext } from 'src/auth/hooks';

import { STORAGE_KEY, fontOptions } from '../config-settings';

// ----------------------------------------------------------------------

export const SettingsContext = createContext(undefined);

export const SettingsConsumer = SettingsContext.Consumer;

// ----------------------------------------------------------------------

export function SettingsProvider({ children, settings }) {
  const values = useLocalStorage(STORAGE_KEY, settings);
  const { setState: setStoredSettings } = values;
  const { user } = useAuthContext();
  const synchronizedUserId = useRef(null);

  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    if (!fontOptions.includes(values.state.fontFamily)) {
      values.setField('fontFamily', settings.fontFamily);
    }
  }, [settings.fontFamily, values]);

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      synchronizedUserId.current = null;
      return undefined;
    }

    axios
      .get(`${endpoints.profile}/preferences`)
      .then((response) => {
        if (!active) return;
        const serverSettings = {
          colorScheme: response.data.colorScheme,
          direction: response.data.direction,
          contrast: response.data.contrast,
          navLayout: response.data.navLayout,
          primaryColor: response.data.primaryColor,
          navColor: response.data.navColor,
          compactLayout: response.data.compactLayout,
          fontFamily: response.data.fontFamily,
        };
        setStoredSettings(serverSettings);
        synchronizedUserId.current = user.id;
      })
      .catch(() => {
        if (active) synchronizedUserId.current = user.id;
      });

    return () => {
      active = false;
    };
  }, [setStoredSettings, user?.id]);

  useEffect(() => {
    if (!user?.id || synchronizedUserId.current !== user.id) return undefined;
    const timeoutId = window.setTimeout(() => {
      axios.put(`${endpoints.profile}/preferences`, values.state).catch(() => undefined);
    }, 700);
    return () => window.clearTimeout(timeoutId);
  }, [user?.id, values.state]);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const memoizedValue = useMemo(
    () => ({
      ...values.state,
      canReset: values.canReset,
      onReset: values.resetState,
      onUpdate: values.setState,
      onUpdateField: values.setField,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
    }),
    [
      values.canReset,
      values.resetState,
      values.setField,
      values.setState,
      values.state,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
    ]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
